// src/pages/AllModelsPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { modelItems } from '../data/SiteData';
import { useAuth } from '../context/AuthContext';


const AllModelsPage = () => {
  const { user: loggedInUser } = useAuth();
  const location = useLocation();
  const preselectedCondition = location.state?.preselectedCondition || null;

  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'Models', path: '/models' },
  ];

  // Accordion expanded state
  const [expandedAccordions, setExpandedAccordions] = useState({
    source: false,
    foci: false,
    condition: !!preselectedCondition,
  });

  const handleAccordionToggle = (panel) => {
    setExpandedAccordions((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  // Snackbar state for upload alert
  const [uploadAlertOpen, setUploadAlertOpen] = useState(false);

  const handleUploadClick = () => {
    setUploadAlertOpen(true);
  };

  const handleUploadAlertClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setUploadAlertOpen(false);
  };

  /* --------------------- SOURCE FILTER --------------------- */
  const sourceGroups = {
    Models: ['ImmunoGit', 'BioModels'],
    'Graph-based': ['Reactome', 'WikiPathways'],
    'Data Resources': ['Immport.org', 'Omnibus', 'GEO'],
  };
  const otherSource = 'Other Source';
  const allSourceOptions = [...Object.values(sourceGroups).flat(), otherSource];
  const initialSourceState = allSourceOptions.reduce((acc, src) => {
    acc[src.toLowerCase()] = true;
    return acc;
  }, {});
  const [sourceChecked, setSourceChecked] = useState(initialSourceState);
  const handleSourceChange = (source) => {
    const key = source.toLowerCase();
    setSourceChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const clearAllSources = () => {
    const cleared = allSourceOptions.reduce(
      (acc, s) => ({ ...acc, [s.toLowerCase()]: false }),
      {}
    );
    setSourceChecked(cleared);
  };
  const selectAllSources = () => {
    const selected = allSourceOptions.reduce(
      (acc, s) => ({ ...acc, [s.toLowerCase()]: true }),
      {}
    );
    setSourceChecked(selected);
  };

  /* --------------------- FOCI FILTER --------------------- */
  const fociOptions = [
    'Adaptive immunity',
    'Antibody-related',
    'Autoimmunity',
    'Cell immunity',
    'Host-pathogen interaction',
    'Immune interaction',
    'Immune regulation',
    'Immune response',
    'Immune signal transduction',
    'Immune systems',
    'Immuno-oncology',
    'Innate immunity',
    'Other Focus',
  ];
  const initialFociState = fociOptions.reduce((acc, f) => {
    acc[f.toLowerCase()] = true;
    return acc;
  }, {});
  const [fociChecked, setFociChecked] = useState(initialFociState);
  const handleFociChange = (foci) => {
    const key = foci.toLowerCase();
    setFociChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const clearAllFoci = () => {
    const cleared = fociOptions.reduce(
      (acc, f) => ({ ...acc, [f.toLowerCase()]: false }),
      {}
    );
    setFociChecked(cleared);
  };
  const selectAllFoci = () => {
    const selected = fociOptions.reduce(
      (acc, f) => ({ ...acc, [f.toLowerCase()]: true }),
      {}
    );
    setFociChecked(selected);
  };

  /* --------------------- CONDITION FILTER --------------------- */
  const conditionOptions = [
    'Autoimmune disease',
    'Cancer',
    'Covid-19',
    'HIV',
    'Infectious Pneumonia',
    'Rheumatoid Arthritis',
    'Sepsis',
    'Systemic lupus erythematosus',
    'Vaccine Response',
    'Other Condition',
  ];
  const initialConditionState = conditionOptions.reduce((acc, c) => {
    acc[c.toLowerCase()] = preselectedCondition
      ? c.toLowerCase() === preselectedCondition.toLowerCase()
      : true;
    return acc;
  }, {});
  const [conditionChecked, setConditionChecked] = useState(initialConditionState);
  const handleConditionChange = (condition) => {
    const key = condition.toLowerCase();
    setConditionChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const clearAllConditions = () => {
    const cleared = conditionOptions.reduce(
      (acc, c) => ({ ...acc, [c.toLowerCase()]: false }),
      {}
    );
    setConditionChecked(cleared);
  };
  const selectAllConditions = () => {
    const selected = conditionOptions.reduce(
      (acc, c) => ({ ...acc, [c.toLowerCase()]: true }),
      {}
    );
    setConditionChecked(selected);
  };

  /* --------------------- SORT & FILTER --------------------- */
  const [sortOrder, setSortOrder] = useState('Latest');

  const filteredItems = useMemo(() => {
    return modelItems
      .filter((item) => {
        const srcKey = item.source.toLowerCase();
        const srcMatch = Object.keys(sourceChecked).includes(srcKey)
          ? sourceChecked[srcKey]
          : true;
        if (!srcMatch) return false;

        const fociMatch =
          item.foci?.some((f) => fociChecked[f.focus.toLowerCase()]) ?? true;
        if (!fociMatch) return false;

        const conditionMatch =
          item.conditions?.some((c) => conditionChecked[c.condition.toLowerCase()]) ?? true;
        if (!conditionMatch) return false;

        return true;
      })
      .sort((a, b) => {
        if (sortOrder === 'Latest') return new Date(b.date) - new Date(a.date);
        return new Date(a.date) - new Date(b.date);
      });
  }, [sourceChecked, fociChecked, conditionChecked, sortOrder]);

  /* --------------------- CUSTOM CHECKBOX --------------------- */
  const CustomCheckboxIcon = ({ disabled }) => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: disabled ? '#eee' : '#fff',
      }}
    />
  );

  const CustomCheckboxCheckedIcon = ({ disabled }) => (
    <Box
      sx={{
        width: 18,
        height: 18,
        border: '1px solid #000',
        backgroundColor: disabled ? '#ccc' : '#489596',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::after': {
          content: '""',
          width: 6,
          height: 10,
          border: 'solid #fff',
          borderWidth: '0 2px 2px 0',
          transform: 'rotate(45deg)',
        },
      }}
    />
  );

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="Models"
        heroDescription="Find models and research data."
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb items={breadcrumbItems} />

      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto', display: 'flex', gap: '32px' }}>
        {/* LEFT COLUMN */}
        <Box sx={{ width: '248px', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden', height: 'fit-content' }}>
          {/* HEADER */}
          <Box sx={{ backgroundColor: 'rgba(72,149,150,0.1)', height: '40px', display: 'flex', alignItems: 'center', px: 2 }}>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: 400, color: '#142434' }}>
              Refine Results
            </Typography>
          </Box>

          {/* SOURCE */}
          <Accordion
            expanded={expandedAccordions.source}
            onChange={() => handleAccordionToggle('source')}
            disableGutters
            square
            sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}
            >
              <Typography sx={{ fontFamily: 'PT Sans', fontSize: '18px', fontWeight: 400, color: '#142434' }}>
                SOURCE
              </Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ px: 2, backgroundColor: '#F3F3F4', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { label: 'Models', options: ['ImmunoGit', 'BioModels'] },
                { label: 'Graph-based', options: ['Reactome', 'WikiPathways'] },
                { label: 'Data Resources', options: ['Immport.org', 'Omnibus', 'GEO'] },
              ].map((section) => (
                <Box key={section.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ fontFamily: 'PT Sans', fontSize: '15px', fontWeight: 600, color: '#142434', mt: 1 }}>
                    {section.label}
                  </Typography>
                  {section.options.map((option) => {
                    const key = option.toLowerCase();
                    return (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={!!sourceChecked[key]}
                            onChange={() => handleSourceChange(option)}
                            icon={<CustomCheckboxIcon disabled={false} />}
                            checkedIcon={<CustomCheckboxCheckedIcon disabled={false} />}
                          />
                        }
                        label={option}
                        sx={{ fontFamily: 'PT Sans', fontSize: '15px', ml: 2 }}
                      />
                    );
                  })}
                </Box>
              ))}
              {['Other Source'].map((option) => {
                const key = option.toLowerCase();
                return (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={!!sourceChecked[key]}
                        onChange={() => handleSourceChange(option)}
                        icon={<CustomCheckboxIcon disabled={false} />}
                        checkedIcon={<CustomCheckboxCheckedIcon disabled={false} />}
                      />
                    }
                    label={option}
                    sx={{ fontFamily: 'PT Sans', fontSize: '15px' }}
                  />
                );
              })}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography onClick={clearAllSources} sx={{ fontFamily: 'PT Sans', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}>
                  Clear all
                </Typography>
                <Typography onClick={selectAllSources} sx={{ fontFamily: 'PT Sans', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}>
                  Select all
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* RESEARCH FOCUS */}
          <Accordion
            expanded={expandedAccordions.foci}
            onChange={() => handleAccordionToggle('foci')}
            disableGutters
            square
            sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}
            >
              <Typography sx={{ fontFamily: 'PT Sans', fontSize: '18px', fontWeight: 400, color: '#142434' }}>
                RESEARCH FOCUS
              </Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ px: 2, backgroundColor: '#F3F3F4', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {fociOptions.map((f) => (
                <FormControlLabel
                  key={f}
                  control={
                    <Checkbox
                      checked={fociChecked[f.toLowerCase()]}
                      onChange={() => handleFociChange(f)}
                      icon={<CustomCheckboxIcon disabled={false} />}
                      checkedIcon={<CustomCheckboxCheckedIcon disabled={false} />}
                    />
                  }
                  label={f}
                  sx={{ fontFamily: 'PT Sans', fontSize: '15px' }}
                />
              ))}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography onClick={clearAllFoci} sx={{ fontFamily: 'PT Sans', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}>
                  Clear all
                </Typography>
                <Typography onClick={selectAllFoci} sx={{ fontFamily: 'PT Sans', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}>
                  Select all
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* CONDITION */}
          <Accordion
            expanded={expandedAccordions.condition}
            onChange={() => handleAccordionToggle('condition')}
            disableGutters
            square
            sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: '#fff', '& .MuiAccordionSummary-content': { margin: 0 }, px: 2, py: 1.5 }}
            >
              <Typography sx={{ fontFamily: 'PT Sans', fontSize: '18px', fontWeight: 400, color: '#142434' }}>
                CONDITION
              </Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ px: 2, backgroundColor: '#F3F3F4', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {conditionOptions.map((c) => (
                <FormControlLabel
                  key={c}
                  control={
                    <Checkbox
                      checked={conditionChecked[c.toLowerCase()]}
                      onChange={() => handleConditionChange(c)}
                      icon={<CustomCheckboxIcon disabled={false} />}
                      checkedIcon={<CustomCheckboxCheckedIcon disabled={false} />}
                    />
                  }
                  label={c}
                  sx={{ fontFamily: 'PT Sans', fontSize: '15px' }}
                />
              ))}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography onClick={clearAllConditions} sx={{ fontFamily: 'PT Sans', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}>
                  Clear all
                </Typography>
                <Typography onClick={selectAllConditions} sx={{ fontFamily: 'PT Sans', fontSize: '18px', textDecoration: 'underline', color: '#489596', cursor: 'pointer' }}>
                  Select all
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* RIGHT COLUMN */}
        <Box sx={{ flex: 1 }}>
          {/* RESULTS HEADER */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434' }}>
              {filteredItems.length} Results
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434' }}>Sort:</Typography>
              <Select size="small" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} sx={{ width: '100px', height: '32px', backgroundColor: '#fff' }}>
                <MenuItem value="Latest">Latest</MenuItem>
                <MenuItem value="Oldest">Oldest</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* EMPTY STATE */}
          {filteredItems.length === 0 && (
            <Typography sx={{ fontFamily: 'PT Sans', fontSize: '20px', fontStyle: 'italic', color: '#999C9F', pl: 5, mb: 2 }}>
              No results have been found. Please edit your filters on the left.
            </Typography>
          )}

          {/* RESULTS */}
          {filteredItems.map((item) => (
            <Card key={item.modelId} sx={{ display: 'flex', alignItems: 'flex-start', mb: 3, border: '1px solid #ddd', borderRadius: '8px', boxShadow: 'none', p: 2 }}>
              <Box component="a" href={item.link} sx={{ display: 'block', mr: 2 }}>
                <Box component="img" src={item.image} alt={item.title} sx={{ width: '120px', height: 'auto', borderRadius: '4px', objectFit: 'cover' }} />
              </Box>
              <CardContent sx={{ padding: 0 }}>
                <Typography component="a" href={item.link} sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 500, color: '#489596', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', mb: '4px', '&:hover': { textDecoration: 'underline' } }}>
                  {item.title}
                </Typography>
                <Typography sx={{ fontFamily: 'PT Sans', fontSize: '14px', color: '#142434', mb: 1 }}>{item.date}</Typography>
                <Typography sx={{ fontFamily: 'PT Sans', fontSize: '15px', color: '#142434', lineHeight: 1.4 }}>{item.description}</Typography>
              </CardContent>
            </Card>
          ))}
          <Typography sx={{ fontFamily: 'PT Sans', fontSize: '16px', color: '#142434' }}>{filteredItems.length} Results</Typography>
        
          {/* CONDITIONAL UPLOAD SECTION */}
{loggedInUser && [3, 4].includes(Number(loggedInUser.accType)) && (
  <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', p: 3, mt: 4, backgroundColor: '#ffffff' }}>
    <Typography sx={{ fontFamily: 'PT Sans', fontSize: '18px', fontWeight: 500, mb: 2 }}>
      Want to upload your model?
    </Typography>
    <Button
      onClick={handleUploadClick}
      sx={{
        width: "240px",
        height: "42px",
        backgroundColor: "#FFFFFF",
        border: "2px solid #489596",
        fontFamily: "Poppins, sans-serif",
        fontSize: "16px",
        fontWeight: 600,
        color: "#489596",
        textTransform: "none",
        boxShadow: "none",
        "&:hover": { backgroundColor: "#ecf1f1" },
      }}
    >
      Upload model
    </Button>
  </Box>
)}



          {/* SNACKBAR ALERT */}
          <Snackbar
            open={uploadAlertOpen}
            autoHideDuration={6000}
            onClose={handleUploadAlertClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleUploadAlertClose}
              severity="info"
              sx={{ width: '100%', border: '1px solid', borderColor: 'info.main', p: 2 }}
            >
              Uploading a model is not available in this demonstration prototype.
            </Alert>
          </Snackbar>
        
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default AllModelsPage;
